import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: string;
  maxHeight?: string;
  className?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Wählen Sie Optionen...",
  label,
  disabled = false,
  error,
  maxHeight = "200px",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // **Position für Portal berechnen**
  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // **Außerhalb klicken schließt Dropdown**
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  // **Gefilterte Optionen basierend auf Suchbegriff**
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // **Option toggle**
  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  // **Alle Optionen wählen/abwählen**
  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(option => option.value));
    }
  };

  // **Einzelne Option entfernen**
  const removeOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  };

  // **Dropdown öffnen**
  const openDropdown = () => {
    if (!disabled) {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  // **Display Text für ausgewählte Werte**
  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return `${selectedValues.length} ausgewählt`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* **LABEL** */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* **MAIN DROPDOWN TRIGGER** */}
      <div
        ref={triggerRef}
        onClick={openDropdown}
        className={`
          w-full px-3 py-2 border rounded-md cursor-pointer transition-all duration-200
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
          ${error ? 'border-red-500' : 'border-gray-300'}
          ${isOpen ? 'ring-2 ring-red-500 border-red-500' : ''}
          text-sm min-h-[38px] flex items-center justify-between
        `}
      >
        {/* **SELECTED VALUES DISPLAY** */}
        <div className="flex-1 flex flex-wrap gap-1">
          {selectedValues.length === 0 ? (
            <span className="text-gray-500">{placeholder}</span>
          ) : selectedValues.length <= 3 ? (
            selectedValues.map(value => {
              const option = options.find(opt => opt.value === value);
              return (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium"
                >
                  {option?.label || value}
                  {!disabled && (
                    <button
                      onClick={(e) => removeOption(value, e)}
                      className="hover:bg-red-200 rounded-full p-0.5 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTimes} className="w-2 h-2" />
                    </button>
                  )}
                </span>
              );
            })
          ) : (
            <span className="text-gray-900 font-medium">
              {selectedValues.length} EK-Bereiche ausgewählt
            </span>
          )}
        </div>

        {/* **DROPDOWN ARROW** */}
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* **ERROR MESSAGE** */}
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {/* **DROPDOWN MENU - PORTAL** */}
      {isOpen && createPortal(
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.15 }}
          ref={dropdownRef}
          className="fixed bg-white border border-gray-300 rounded-md shadow-lg z-[9999]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: '300px'
          }}
        >
          {/* **SEARCH INPUT** */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* **SELECT ALL OPTION** */}
          <div className="border-b border-gray-200">
            <div
              onClick={toggleAll}
              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
            >
              <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${
                selectedValues.length === options.length ? 'bg-red-600 border-red-600' : 'border-gray-300'
              }`}>
                {selectedValues.length === options.length && (
                  <FontAwesomeIcon icon={faCheck} className="text-white w-2 h-2" />
                )}
              </div>
              <span className="font-medium">
                {selectedValues.length === options.length ? 'Alle abwählen' : 'Alle auswählen'}
              </span>
            </div>
          </div>

          {/* **OPTIONS LIST** */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Keine Optionen gefunden
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${
                    selectedValues.includes(option.value) ? 'bg-red-600 border-red-600' : 'border-gray-300'
                  }`}>
                    {selectedValues.includes(option.value) && (
                      <FontAwesomeIcon icon={faCheck} className="text-white w-2 h-2" />
                    )}
                  </div>
                  <span>{option.label}</span>
                </div>
              ))
            )}
          </div>
        </motion.div>,
        document.body
      )}
    </div>
  );
};

export default MultiSelectDropdown;
